package diana.soleil.design.iraniancanadienbazzar.feature.feed

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsEvents
import diana.soleil.design.iraniancanadienbazzar.core.common.AnalyticsTracker
import diana.soleil.design.iraniancanadienbazzar.core.common.AppError
import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.BlockRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.BusinessRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.DealsRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.EventRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.FeatureFlags
import diana.soleil.design.iraniancanadienbazzar.core.data.FollowRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.Deal
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.mapLatest
import diana.soleil.design.iraniancanadienbazzar.core.data.CityController
import diana.soleil.design.iraniancanadienbazzar.core.data.ImagePickController
import diana.soleil.design.iraniancanadienbazzar.core.data.PostRepository
import diana.soleil.design.iraniancanadienbazzar.core.data.ReportRepository
import diana.soleil.design.iraniancanadienbazzar.core.model.DiscoveryRanking
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import diana.soleil.design.iraniancanadienbazzar.core.model.Post
import diana.soleil.design.iraniancanadienbazzar.core.model.PostType
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/** Report lifecycle for a post (mirrors the DS `ReportStatus`, with a sign-in gate for guests). */
enum class PostReportStatus { Idle, Sending, Sent, SignInRequired, Failed }

/** Posts fetched per page; "load older" grows the live query window by this much. */
private const val PAGE_SIZE = 20

/** How the feed is ordered. Newest = the natural chronological feed (default). */
enum class FeedSort { Newest, MostLiked, Photos }

/**
 * Feed lenses (Stitch c8b1f8fb chip row: All · Events · Deals · New · …). The community lenses
 * show the metro's upcoming events, live merchant offers and newest listings as feed cards; the
 * post lenses narrow member posts by [postType]; All interleaves both.
 */
enum class FeedFilter(val postType: PostType? = null) {
    All,
    Events,
    Deals,
    New,
    Update(PostType.General),
    Question(PostType.Question),
    Recommendation(PostType.Recommendation),
    ;

    /** Lenses whose content isn't member posts (no sort row, no "load older"). */
    val isCommunity: Boolean get() = this == Events || this == Deals || this == New
}

/** One row of the feed list: a member post or a community card woven between posts. */
sealed interface FeedEntry {
    val key: String

    data class PostEntry(val post: Post) : FeedEntry {
        override val key: String get() = "post_" + post.id
    }

    data class EventEntry(val event: Event, val goingCount: Int) : FeedEntry {
        override val key: String get() = "event_" + event.id
    }

    data class DealEntry(val deal: Deal, val business: Business?) : FeedEntry {
        override val key: String get() = "deal_" + deal.id
    }

    data class BusinessEntry(val business: Business) : FeedEntry {
        override val key: String get() = "biz_" + business.id
    }
}

/** Feed state: metro-scoped posts, client-filtered by [typeFilter] (null = all) + sorted by [sort]. */
data class FeedUiState(
    val loading: Boolean = true,
    val posts: List<Post> = emptyList(),
    val filter: FeedFilter = FeedFilter.All,
    val sort: FeedSort = FeedSort.Newest,
    val error: Boolean = false,
    val posting: Boolean = false,
    /** Guest tried to publish — the route sends them to sign-in. */
    val signInRequired: Boolean = false,
    val postFailed: Boolean = false,
    /** Deleting or editing a post failed — the screen shows an inline error. */
    val actionFailed: Boolean = false,
    /** A photo is attached to the composer draft (uploaded with the post on publish). */
    val photoAttached: Boolean = false,
    /** The last page was full, so older posts may exist — show "load older". */
    val hasMore: Boolean = false,
    /** Ids of posts the signed-in user has liked / privately bookmarked. */
    val likedPostIds: Set<String> = emptySet(),
    val savedPostIds: Set<String> = emptySet(),
    /** Uids the signed-in user has blocked — their posts are dropped from [filtered]. */
    val blockedUids: Set<String> = emptySet(),
    val reportStatus: PostReportStatus = PostReportStatus.Idle,
    /** The signed-in uid, or null — a post authored by it shows a delete affordance instead of report. */
    val currentUid: String? = null,
    /** Community content woven into the feed (additive: a failure just leaves these empty). */
    val upcomingEvents: List<Event> = emptyList(),
    /** RSVP "going" counts for [upcomingEvents], by event id. */
    val goingCounts: Map<String, Int> = emptyMap(),
    val deals: List<Deal> = emptyList(),
    /** The merchants behind [deals], by id — supplies the card's logo/cover. */
    val dealBusinesses: Map<String, Business> = emptyMap(),
    /** Newest approved listings in the active metro ("New in your mahaleh"). */
    val newBusinesses: List<Business> = emptyList(),
    /** Businesses the signed-in user follows (Follow buttons on new-listing cards + Suggested). */
    val followingIds: Set<String> = emptySet(),
) {
    val filtered: List<Post>
        get() = posts
            .filter { post ->
                post.authorUid !in blockedUids &&
                    !filter.isCommunity &&
                    (filter.postType == null || post.type == filter.postType)
            }
            .let { visible ->
                when (sort) {
                    FeedSort.Newest -> visible // repository order: createdAt descending
                    FeedSort.MostLiked ->
                        visible.sortedWith(
                            compareByDescending<Post> { it.likeCount }
                                .thenByDescending { it.createdAtMillis ?: 0L },
                        )
                    FeedSort.Photos -> visible.sortedWith(DiscoveryRanking.postPhotoFirstComparator())
                }
            }

    /** Community cards, round-robin across events / deals / new listings so the mix stays varied. */
    private val communityCards: List<FeedEntry>
        get() {
            val queues = listOf(
                upcomingEvents.take(CARDS_PER_KIND).map { FeedEntry.EventEntry(it, goingCounts[it.id] ?: 0) },
                deals.take(CARDS_PER_KIND).map { FeedEntry.DealEntry(it, dealBusinesses[it.businessId]) },
                newBusinesses.take(CARDS_PER_KIND).map { FeedEntry.BusinessEntry(it) },
            )
            val out = mutableListOf<FeedEntry>()
            var index = 0
            while (queues.any { index < it.size }) {
                queues.forEach { queue -> queue.getOrNull(index)?.let(out::add) }
                index++
            }
            return out
        }

    /**
     * What the list renders for the active [filter]. On All, a community card follows every
     * [POSTS_PER_CARD] posts; where there are no posts yet (a new metro) the cards ARE the feed
     * rather than an empty state.
     */
    val entries: List<FeedEntry>
        get() = when (filter) {
            FeedFilter.Events -> upcomingEvents.map { FeedEntry.EventEntry(it, goingCounts[it.id] ?: 0) }
            FeedFilter.Deals -> deals.map { FeedEntry.DealEntry(it, dealBusinesses[it.businessId]) }
            FeedFilter.New -> newBusinesses.map { FeedEntry.BusinessEntry(it) }
            FeedFilter.All -> {
                val cards = communityCards.iterator()
                val posts = filtered.map { FeedEntry.PostEntry(it) }
                if (posts.isEmpty()) {
                    cards.asSequence().toList()
                } else {
                    buildList {
                        posts.forEachIndexed { i, entry ->
                            add(entry)
                            if ((i + 1) % POSTS_PER_CARD == 0 && cards.hasNext()) add(cards.next())
                        }
                        while (cards.hasNext()) add(cards.next())
                    }
                }
            }
            else -> filtered.map { FeedEntry.PostEntry(it) }
        }

    /** "Trending near you": the events most people are going to, soonest first among ties. */
    val trending: List<Event>
        get() = upcomingEvents
            .sortedWith(compareByDescending<Event> { goingCounts[it.id] ?: 0 }.thenBy { it.startsAtMillis })
            .take(TRENDING_COUNT)

    /** "Suggested for you": new listings the user doesn't follow yet. */
    val suggested: List<Business>
        get() = newBusinesses.filter { it.id !in followingIds }.take(SUGGESTED_COUNT)
}

private const val CARDS_PER_KIND = 3
private const val POSTS_PER_CARD = 2
private const val TRENDING_COUNT = 3
private const val SUGGESTED_COUNT = 3

/**
 * Community feed (Stitch `c8b1f8fb`): streams the active metro's posts (re-queries on metro change,
 * same reactive pattern as Home) and publishes new ones. Reading is public; posting needs a real
 * account.
 */
@OptIn(ExperimentalCoroutinesApi::class)
class FeedViewModel(
    private val postRepository: PostRepository,
    private val reportRepository: ReportRepository,
    private val blockRepository: BlockRepository,
    cityController: CityController,
    private val analytics: AnalyticsTracker,
    private val imagePicker: ImagePickController,
    eventRepository: EventRepository,
    dealsRepository: DealsRepository,
    businessRepository: BusinessRepository,
    private val followRepository: FollowRepository,
    featureFlags: FeatureFlags,
) : ViewModel() {

    private val filter = MutableStateFlow(FeedFilter.All)
    private val sortFlow = MutableStateFlow(FeedSort.Newest)
    private val posting = MutableStateFlow(false)
    private val postGate = MutableStateFlow<AppError?>(null)
    private val reportStatus = MutableStateFlow(PostReportStatus.Idle)
    private val attachedPhoto = MutableStateFlow<PickedImage?>(null)
    private val pageSize = MutableStateFlow(PAGE_SIZE)
    private val actionFailed = MutableStateFlow(false)
    // Bumped by retry() to force the post stream to re-subscribe after a load failure.
    private val reloadKey = MutableStateFlow(0)

    private data class ComposeState(
        val posting: Boolean,
        val gate: AppError?,
        val report: PostReportStatus,
        val photoAttached: Boolean,
        val actionFailed: Boolean,
    )

    private val composeState =
        combine(posting, postGate, reportStatus, attachedPhoto, actionFailed) { p, g, r, photo, failed ->
            ComposeState(p, g, r, photo != null, failed)
        }

    private data class Community(
        val events: List<Event>,
        val going: Map<String, Int>,
        val deals: List<Deal>,
        val dealBusinesses: Map<String, Business>,
        val newest: List<Business>,
        val following: Set<String>,
    )

    // Everything below is additive: flag-off or a failed stream leaves that kind of card out and
    // never errors the feed.
    private val communityEvents: Flow<Pair<List<Event>, Map<String, Int>>> =
        featureFlags.available.flatMapLatest { flags ->
            if (!flags.events) flowOf(emptyList())
            else cityController.activeMetroId
                .flatMapLatest { metro -> eventRepository.observeUpcomingEvents(metro) }
                .map { (it as? DataResult.Success)?.data ?: emptyList() }
                .catch { emit(emptyList()) }
        }.mapLatest { events ->
            val shown = events.take(COMMUNITY_EVENTS)
            val going = if (shown.isEmpty()) emptyMap()
            else runCatching { eventRepository.rsvpCounts(shown.map { it.id }) }.getOrDefault(emptyMap())
            shown to going
        }

    private val communityDeals: Flow<Pair<List<Deal>, Map<String, Business>>> =
        featureFlags.available.flatMapLatest { flags ->
            if (!flags.deals) flowOf(emptyList())
            else dealsRepository.observeActiveDeals(COMMUNITY_DEALS)
                .map { (it as? DataResult.Success)?.data ?: emptyList() }
                .catch { emit(emptyList()) }
        }.mapLatest { deals ->
            val businesses = if (deals.isEmpty()) emptyList()
            else runCatching { businessRepository.getBusinesses(deals.map { it.businessId }.distinct()) }.getOrDefault(emptyList())
            deals to businesses.associateBy { it.id }
        }

    private val newestBusinesses: Flow<List<Business>> =
        combine(cityController.activeMetroId, cityController.activeCountry) { metro, country -> metro to country }
            .flatMapLatest { (metro, country) ->
                businessRepository.observeNewest(metro, country.code, COMMUNITY_NEW)
                    .map { (it as? DataResult.Success)?.data ?: emptyList() }
                    .catch { emit(emptyList()) }
            }

    private val following: Flow<Set<String>> =
        followRepository.observeFollowing()
            .map { (it as? DataResult.Success)?.data ?: emptySet() }
            .catch { emit(emptySet()) }

    private val community: Flow<Community> =
        combine(communityEvents, communityDeals, newestBusinesses, following) { (events, going), (deals, dealBiz), newest, follows ->
            Community(events, going, deals, dealBiz, newest, follows)
        }

    val state: StateFlow<FeedUiState> =
        combine(
            combine(cityController.activeMetroId, pageSize, reloadKey) { metro, size, _ -> metro to size }
                .flatMapLatest { (metro, size) -> postRepository.observePosts(metro, size).map { it to size } },
            combine(filter, sortFlow) { lens, sort -> lens to sort },
            composeState,
            combine(
                postRepository.observeLikedPostIds(),
                postRepository.observeSavedPostIds(),
                blockRepository.observeBlockedUids(),
            ) { liked, saved, blocked -> Triple(liked, saved, blocked) },
            community,
        ) { (result, size), (lens, sort), compose, (liked, saved, blocked), community ->
            when (result) {
                is DataResult.Success -> FeedUiState(
                    loading = false,
                    posts = result.data,
                    filter = lens,
                    sort = sort,
                    posting = compose.posting,
                    signInRequired = compose.gate is AppError.Unauthorized,
                    postFailed = compose.gate != null && compose.gate !is AppError.Unauthorized,
                    actionFailed = compose.actionFailed,
                    photoAttached = compose.photoAttached,
                    hasMore = result.data.size >= size,
                    likedPostIds = liked,
                    savedPostIds = saved,
                    blockedUids = blocked,
                    reportStatus = compose.report,
                    currentUid = postRepository.currentUid,
                    upcomingEvents = community.events,
                    goingCounts = community.going,
                    deals = community.deals,
                    dealBusinesses = community.dealBusinesses,
                    newBusinesses = community.newest,
                    followingIds = community.following,
                )
                is DataResult.Failure -> FeedUiState(loading = false, error = true)
            }
        }.stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), FeedUiState())

    /** Grows the observed window by a page ("load older") — the snapshot stream stays live. */
    fun loadOlder() {
        pageSize.value += PAGE_SIZE
    }

    /** Re-subscribes the post stream after a load failure (wired to the error-state Retry). */
    fun retry() {
        reloadKey.value += 1
    }

    /** Toggles the caller's like on [postId]; guests bounce to sign-in via the gate. */
    fun toggleLike(postId: String, liked: Boolean) {
        viewModelScope.launch {
            val result = postRepository.setLiked(postId, !liked)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) {
                postGate.value = result.error
            }
        }
    }

    /** Toggles the caller's private bookmark on [postId]; guests bounce to sign-in via the gate. */
    fun toggleSave(postId: String, saved: Boolean) {
        viewModelScope.launch {
            val result = postRepository.setSaved(postId, !saved)
            if (result is DataResult.Failure && result.error is AppError.Unauthorized) {
                postGate.value = result.error
            }
        }
    }

    fun setFilter(lens: FeedFilter) {
        filter.value = lens
    }

    /** Follows/unfollows [businessId] from a feed card; guests bounce to sign-in via the gate. */
    fun toggleFollow(businessId: String, following: Boolean) {
        viewModelScope.launch {
            val result = followRepository.setFollowing(businessId, !following)
            if (result is DataResult.Failure) {
                if (result.error is AppError.Unauthorized) postGate.value = result.error
                else actionFailed.value = true
            }
        }
    }

    fun setSort(sort: FeedSort) {
        sortFlow.value = sort
    }

    fun publish(type: PostType, text: String, activeMetroId: String?) {
        // A post needs text OR a photo — image-first posts are valid (createPost forwards the photo).
        if ((text.isBlank() && attachedPhoto.value == null) || posting.value) return
        viewModelScope.launch {
            posting.value = true
            postGate.value = null
            when (val result = postRepository.createPost(type, text, activeMetroId, attachedPhoto.value)) {
                is DataResult.Success -> {
                    analytics.track(AnalyticsEvents.POST_CREATE)
                    attachedPhoto.value = null
                }
                is DataResult.Failure -> postGate.value = result.error
            }
            posting.value = false
        }
    }

    /** Opens the system photo picker and attaches the result to the composer draft. */
    fun attachPhoto() {
        viewModelScope.launch {
            imagePicker.pick()?.let { attachedPhoto.value = it }
        }
    }

    fun removePhoto() {
        attachedPhoto.value = null
    }

    fun consumeGate() {
        postGate.value = null
    }

    /** Files a moderation report against [postId]; guests trip [PostReportStatus.SignInRequired]. */
    fun report(postId: String, reason: String) {
        if (reportStatus.value == PostReportStatus.Sending) return
        viewModelScope.launch {
            reportStatus.value = PostReportStatus.Sending
            reportStatus.value = when (val result = reportRepository.fileReport("post", postId, reason)) {
                is DataResult.Success -> PostReportStatus.Sent
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) PostReportStatus.SignInRequired
                    else PostReportStatus.Failed
            }
        }
    }

    fun resetReport() {
        reportStatus.value = PostReportStatus.Idle
    }

    /**
     * Blocks [targetUid] — their posts, comments, listings and threads vanish client-side (the live
     * block-list stream feeds every filter). Guests bounce to sign-in via the gate; the target is
     * never notified.
     */
    fun blockUser(targetUid: String, targetName: String) {
        viewModelScope.launch {
            actionFailed.value = false
            when (val result = blockRepository.block(targetUid, targetName)) {
                is DataResult.Success -> Unit
                is DataResult.Failure ->
                    if (result.error is AppError.Unauthorized) postGate.value = result.error
                    else actionFailed.value = true
            }
        }
    }

    /** Deletes the caller's own post; the live feed drops it on success, failures flag the UI. */
    fun deletePost(postId: String) {
        viewModelScope.launch {
            actionFailed.value = false
            if (postRepository.deletePost(postId) is DataResult.Failure) actionFailed.value = true
        }
    }

    /** Edits the caller's own post; the live feed reflects the new type/text on success. */
    fun updatePost(postId: String, type: PostType, text: String) {
        if (text.isBlank()) return
        viewModelScope.launch {
            actionFailed.value = false
            if (postRepository.updatePost(postId, type, text) is DataResult.Failure) actionFailed.value = true
        }
    }
}

private const val COMMUNITY_EVENTS = 6
private const val COMMUNITY_DEALS = 6
private const val COMMUNITY_NEW = 6
